import React, { useState } from 'react';
import { useFinance } from '../contexts/FinanceContext';
import Modal from './Modal';

function CategoryManager() {
  const { state, addCategory, updateCategory, deleteCategory, addTag, updateTag, deleteTag, addMode, updateMode, deleteMode } = useFinance();
  const [activeTab, setActiveTab] = useState('categories'); // categories, tags, modes
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalType, setModalType] = useState('category');
  const [editingItem, setEditingItem] = useState(null);
  
  const [categoryForm, setCategoryForm] = useState({ name: '', icon: '📦', color: '#3b82f6' });
  const [tagForm, setTagForm] = useState({ name: '', color: '#3b82f6' });
  const [modeForm, setModeForm] = useState({ 
    name: '', 
    type: 'other', 
    icon: '💳',
    currency: state.currency,
    exchangeRate: '1'
  });

  const emojis = ['📦', '🍔', '🚗', '🛍️', '🎬', '💡', '🏥', '💰', '💻', '🏠', '✈️', '📚', '🎮', '💪', '🎨', '🎵', '☕', '🍕', '👕', '💄', '🎁', '📱', '🔧', '🌳'];
  const colors = ['#ef4444', '#f97316', '#eab308', '#22c55e', '#10b981', '#06b6d4', '#3b82f6', '#6366f1', '#8b5cf6', '#d946ef', '#ec4899', '#64748b'];
  const modeIcons = ['💵', '💳', '🏦', '📱', '🪙', '🧾'];

  const openModal = (type, item = null) => {
    setModalType(type);
    setEditingItem(item);
    if (type === 'category') {
      setCategoryForm(item ? { name: item.name, icon: item.icon, color: item.color } : { name: '', icon: '📦', color: '#3b82f6' });
    } else if (type === 'tag') {
      setTagForm(item ? { name: item.name, color: item.color } : { name: '', color: '#3b82f6' });
    } else {
      setModeForm(item 
        ? { 
            name: item.name, 
            type: item.type, 
            icon: item.icon,
            currency: item.currency || state.currency,
            exchangeRate: item.exchangeRate ? item.exchangeRate.toString() : '1'
          } 
        : { 
            name: '', 
            type: 'other', 
            icon: '💳',
            currency: state.currency,
            exchangeRate: '1'
          }
      );
    }
    setIsModalOpen(true);
  };

  const handleCategorySubmit = (e) => {
    e.preventDefault();
    if (editingItem) updateCategory({ ...editingItem, ...categoryForm });
    else addCategory(categoryForm);
    setIsModalOpen(false);
  };

  const handleTagSubmit = (e) => {
    e.preventDefault();
    if (editingItem) updateTag({ ...editingItem, ...tagForm });
    else addTag(tagForm);
    setIsModalOpen(false);
  };

  const handleModeSubmit = (e) => {
    e.preventDefault();
    const data = {
      ...modeForm,
      exchangeRate: parseFloat(modeForm.exchangeRate) || 1
    };
    if (editingItem) updateMode({ ...editingItem, ...data });
    else addMode(data);
    setIsModalOpen(false);
  };

  return (
    <div className="p-4 lg:p-6 space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-white">Organization</h2>
          <p className="text-slate-400 mt-1">Manage categories, tags, and payment modes</p>
        </div>
        <button
          onClick={() => openModal(activeTab === 'categories' ? 'category' : activeTab === 'tags' ? 'tag' : 'mode')}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-gradient-to-r from-primary-600 to-primary-500 text-white font-medium hover:from-primary-500 hover:to-primary-400 transition-all"
        >
          <span>➕</span> Add {activeTab === 'categories' ? 'Category' : activeTab === 'tags' ? 'Tag' : 'Mode'}
        </button>
      </div>

      <div className="flex gap-2 border-b border-slate-700 pb-2">
        <button onClick={() => setActiveTab('categories')} className={`px-4 py-2 rounded-lg font-medium transition-all ${activeTab === 'categories' ? 'bg-primary-500/20 text-primary-400' : 'text-slate-400 hover:text-white'}`}>📁 Categories</button>
        <button onClick={() => setActiveTab('tags')} className={`px-4 py-2 rounded-lg font-medium transition-all ${activeTab === 'tags' ? 'bg-primary-500/20 text-primary-400' : 'text-slate-400 hover:text-white'}`}>🏷️ Tags</button>
        <button onClick={() => setActiveTab('modes')} className={`px-4 py-2 rounded-lg font-medium transition-all ${activeTab === 'modes' ? 'bg-primary-500/20 text-primary-400' : 'text-slate-400 hover:text-white'}`}>💳 Modes</button>
      </div>

      {activeTab === 'categories' && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {state.categories.map(category => (
            <div key={category.id} className="bg-slate-800/50 border border-slate-700 rounded-2xl p-4 flex justify-between items-center group">
               <div className="flex items-center gap-3">
                   <div className="w-10 h-10 rounded-lg flex items-center justify-center text-xl" style={{ backgroundColor: `${category.color}20` }}>{category.icon}</div>
                   <span className="text-white font-medium">{category.name}</span>
               </div>
               <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                   <button onClick={() => openModal('category', category)} className="text-slate-400 hover:text-white">✏️</button>
                   <button onClick={() => { if(window.confirm('Delete?')) deleteCategory(category.id); }} className="text-slate-400 hover:text-rose-400">🗑️</button>
               </div>
            </div>
          ))}
        </div>
      )}

      {activeTab === 'tags' && (
        <div className="flex flex-wrap gap-3">
          {state.tags.map(tag => (
            <div key={tag.id} className="px-3 py-1.5 rounded-lg border flex items-center gap-2 group" style={{ backgroundColor: `${tag.color}20`, borderColor: tag.color }}>
                <span className="font-medium" style={{ color: tag.color }}>{tag.name}</span>
                <button onClick={() => openModal('tag', tag)} className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-white">✏️</button>
                <button onClick={() => { if(window.confirm('Delete?')) deleteTag(tag.id); }} className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-rose-400">🗑️</button>
            </div>
          ))}
        </div>
      )}

      {activeTab === 'modes' && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
           {state.modes.map(mode => (
             <div key={mode.id} className="bg-slate-800/50 border border-slate-700 rounded-2xl p-4 flex flex-col gap-2 group">
                <div className="flex justify-between items-start">
                    <div className="flex items-center gap-3">
                        <span className="text-2xl">{mode.icon}</span>
                        <div>
                            <p className="text-white font-medium">{mode.name}</p>
                            <p className="text-xs text-slate-500">
                                {mode.currency || state.currency} • Rate: {mode.exchangeRate || 1}
                            </p>
                        </div>
                    </div>
                </div>
                <div className="flex justify-end gap-2 mt-2 opacity-0 group-hover:opacity-100 transition-opacity">
                   <button onClick={() => openModal('mode', mode)} className="text-sm px-3 py-1 bg-slate-700 rounded-lg text-slate-300 hover:text-white">Edit</button>
                   <button onClick={() => { if(window.confirm('Delete?')) deleteMode(mode.id); }} className="text-sm px-3 py-1 bg-rose-500/10 rounded-lg text-rose-400 hover:bg-rose-500/20">Delete</button>
                </div>
             </div>
           ))}
        </div>
      )}

      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} title={`Add/Edit ${activeTab.slice(0, -1)}`}>
         {modalType === 'category' && (
             <form onSubmit={handleCategorySubmit} className="space-y-4">
                 <input type="text" value={categoryForm.name} onChange={e => setCategoryForm({...categoryForm, name: e.target.value})} placeholder="Name" className="w-full px-4 py-2 bg-slate-700 rounded-lg text-white" />
                 <div className="flex flex-wrap gap-2">{emojis.map(e => <button key={e} type="button" onClick={() => setCategoryForm({...categoryForm, icon: e})} className={`w-8 h-8 rounded ${categoryForm.icon === e ? 'bg-primary-500' : 'bg-slate-700'}`}>{e}</button>)}</div>
                 <div className="flex flex-wrap gap-2">{colors.map(c => <button key={c} type="button" onClick={() => setCategoryForm({...categoryForm, color: c})} className={`w-6 h-6 rounded-full border-2 ${categoryForm.color === c ? 'border-white' : 'border-transparent'}`} style={{ backgroundColor: c }} />)}</div>
                 <button type="submit" className="w-full py-2 bg-primary-600 text-white rounded-lg">Save</button>
             </form>
         )}

         {modalType === 'tag' && (
             <form onSubmit={handleTagSubmit} className="space-y-4">
                 <input type="text" value={tagForm.name} onChange={e => setTagForm({...tagForm, name: e.target.value})} placeholder="Name" className="w-full px-4 py-2 bg-slate-700 rounded-lg text-white" />
                 <div className="flex flex-wrap gap-2">{colors.map(c => <button key={c} type="button" onClick={() => setTagForm({...tagForm, color: c})} className={`w-6 h-6 rounded-full border-2 ${tagForm.color === c ? 'border-white' : 'border-transparent'}`} style={{ backgroundColor: c }} />)}</div>
                 <button type="submit" className="w-full py-2 bg-primary-600 text-white rounded-lg">Save</button>
             </form>
         )}

         {modalType === 'mode' && (
             <form onSubmit={handleModeSubmit} className="space-y-4">
                 <div>
                    <label className="block text-sm text-slate-300 mb-1">Name</label>
                    <input type="text" value={modeForm.name} onChange={e => setModeForm({...modeForm, name: e.target.value})} placeholder="e.g. US Account" required className="w-full px-4 py-2 bg-slate-700 rounded-lg text-white" />
                 </div>
                 
                 <div className="grid grid-cols-2 gap-4">
                     <div>
                        <label className="block text-sm text-slate-300 mb-1">Currency Symbol</label>
                        <input type="text" value={modeForm.currency} onChange={e => setModeForm({...modeForm, currency: e.target.value})} placeholder="e.g. $" required className="w-full px-4 py-2 bg-slate-700 rounded-lg text-white" />
                     </div>
                     <div>
                        <label className="block text-sm text-slate-300 mb-1">Exchange Rate (vs {state.currency})</label>
                        <input type="number" step="0.0001" value={modeForm.exchangeRate} onChange={e => setModeForm({...modeForm, exchangeRate: e.target.value})} placeholder="e.g. 85" required className="w-full px-4 py-2 bg-slate-700 rounded-lg text-white" />
                         <p className="text-xs text-slate-400 mt-1">1 {modeForm.currency || 'Unit'} = {modeForm.exchangeRate || 1} {state.currency}</p>
                     </div>
                 </div>

                 <div>
                    <label className="block text-sm text-slate-300 mb-1">Icon</label>
                    <div className="flex flex-wrap gap-2">{modeIcons.map(icon => <button key={icon} type="button" onClick={() => setModeForm({...modeForm, icon})} className={`w-10 h-10 rounded ${modeForm.icon === icon ? 'bg-primary-500' : 'bg-slate-700'}`}>{icon}</button>)}</div>
                 </div>
                 
                 <button type="submit" className="w-full py-2 bg-primary-600 text-white rounded-lg">Save</button>
             </form>
         )}
      </Modal>
    </div>
  );
}

export default CategoryManager;
